import {
  Box,
  Card,
  Typography,
  Container,
  Divider,
  FormControl,
  FormLabel
} from '@mui/material';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles, useTheme } from '@mui/styles';
import jwt_decode from 'jwt-decode';
import { Helmet } from 'react-helmet-async';
import bookingDliteLogo from 'src/assets/images/bookind-d-lite.png';
import {
  AUTH_CONFIG,
  MESSAGE,
  ROLES,
  STATUS_404,
  I18N,
  DEV_ENVIRONMENT
} from 'src/shared/constants/constants';
import {
  getLoginDetails,
  selectUserState,
  getLogOutUser,
  setLoginProvider
} from './services/userSlice';
import { styled } from '@mui/material/styles';
import * as ROUTES from '../../shared/constants/routes';
import { useNavigate } from 'react-router';
import { useEffect, useState } from 'react';
import { loginRequest } from './config/authConfig';
import { clearStorage } from 'src/shared/utils/storage';
import { useMsal } from '@azure/msal-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'src/providers/AuthguardContext';
import LoginByUserNamePassword from './LoginByUserNamePassword';
import React from 'react';

const useStyles = makeStyles({
  msLoginButton: {
    backgroundColor: 'rgb(255, 255, 255)',
    display: 'inline-flex',
    alignItems: 'center',
    color: 'rgba(0, 0, 0, 0.54)',
    boxShadow:
      'rgb(0 0 0 / 24%) 0px 2px 2px 0px, rgb(0 0 0 / 24%) 0px 0px 1px 0px',
    paddingTop: '10px',
    paddingBottom: '10px',
    paddingLeft: '20px',
    paddingRight: '20px',
    borderRadius: '2px',
    border: '1px solid transparent',
    fontSize: '14px',
    fontWeight: 500,
    fontFamily: 'Roboto, sans-serif',
    cursor: 'pointer',
    width: '50%'
  },
  msLogo: { marginRight: '21px' }
});

const Login = () => {
  // Constants
  const theme = useTheme();
  const navigate = useNavigate();
  const [loginType, setLoginType] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const { login, logout, accessToken } = useAuth();
  const { instance } = useMsal();
  const dispatch = useDispatch<any>();
  const { t } = useTranslation(I18N.DEFAULT);
  const classes = useStyles();
  const { userData, errorObj, loginUserRole } = useSelector(selectUserState);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState<string>('');
  const [showPassword, setShowPassword] = React.useState(false);
  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const MainContent = styled(Box)(
    ({ theme }) => `
      height: 100%;
      display: flex;
      flex: 1;
      overflow: auto;
      flex-direction: column;
      align-items: center;
      justify-content: center;
  `
  );

  useEffect(() => {
    if (window.location.pathname === ROUTES.ADMIN_LOGIN) {
      setLoginType(AUTH_CONFIG.LOGIN_TYPE_ADMIN);
    } else {
      setLoginType(AUTH_CONFIG.LOGIN_TYPE_USER);
    }
  }, [window.location.pathname]);

  useEffect(() => {
    if (!accessToken) {
      dispatch(getLogOutUser(null));
      clearStorage();
    }
    setErrorMessage('');
  }, []);

  useEffect(() => {
    if (userData?.token) {
      login(userData?.token);
      if (loginType === AUTH_CONFIG.LOGIN_TYPE_ADMIN) {
        navigate(`${ROUTES.ADMIN_LOGIN}${ROUTES.ORGANIZATION}`);
      } else {
        navigate(`${ROUTES.TENANT_LOGIN}${ROUTES.ORGANIZATION}`);
      }
    }
  }, [userData, accessToken]);

  // Error Handling In Token Verification
  useEffect(() => {
    if (
      errorObj &&
      errorObj.hasOwnProperty(MESSAGE) &&
      errorObj?.message?.includes(STATUS_404)
    ) {
      const statusCode = errorObj?.message;
      if (statusCode.includes(STATUS_404)) {
        logout();
        navigate(ROUTES.LOGIN);
      }
    } else if (errorObj) {
      setErrorMessage(errorObj.message);
    }
  }, [errorObj]);

  // Methods
  // Payload Object For oauth-login api
  const getUserObj = (
    access_token: string,
    tokenType: string,
    loginType: string,
    username: string,
    password: string
  ) => ({
    token: access_token,
    tokenType: tokenType,
    loginType: loginType,
    username: username,
    password: password
  });

  // Google Login SSO
  const responseonSuccessGoogle = (response: any) => {
    if (response) {
      dispatch(setLoginProvider(AUTH_CONFIG.LOGIN_TYPE_GOOGLE));
      dispatch(
        getLoginDetails(
          getUserObj(
            response.credential,
            AUTH_CONFIG.LOGIN_TYPE_GOOGLE,
            loginType,
            '',
            ''
          )
        )
      );
    }
  };

  const responseonFailureGoogle = (response: any) => {
    // eslint-disable-next-line no-console
    dispatch(setLoginProvider(AUTH_CONFIG.LOGIN_TYPE_GOOGLE));
  };

  // Microsoft Login SSO
  const handleLogin = (instance: any) => {
    instance
      .loginPopup(loginRequest)
      .then((response: any) => {
        const decodedObj = getDecodedToken(response.idToken) as any;
        decodedObj.access = response.accessToken;
        dispatch(setLoginProvider(AUTH_CONFIG.LOGIN_TYPE_MICROSOFT));
        dispatch(
          getLoginDetails(
            getUserObj(
              decodedObj.access,
              AUTH_CONFIG.LOGIN_TYPE_MICROSOFT,
              loginType,
              '',
              ''
            )
          )
        );
      })
      .catch((e: any) => {
        // eslint-disable-next-line no-console
        dispatch(setLoginProvider(AUTH_CONFIG.LOGIN_TYPE_MICROSOFT));
        console.error(e);
      });
  };

  //JWT Token Decoder
  const getDecodedToken = (encodedStr: string) => {
    return jwt_decode(encodedStr);
  };

  //userid password

  const submitLoginDetails = async () => {
    if (email.trim().length === 0 || password.trim().length === 0) {
      setErrorMessage('Enter valid credentials');
    } else {
      dispatch(setLoginProvider(AUTH_CONFIG.LOGIN_TYPE_BASIC_AUTH));
      dispatch(
        getLoginDetails(
          getUserObj(
            '',
            AUTH_CONFIG.LOGIN_TYPE_BASIC_AUTH,
            loginType,
            email,
            password
          )
        )
      );
    }
  };

  // HTML
  return (
    <>
      <Helmet>
        <title>Login</title>
      </Helmet>
      <MainContent>
        <Container maxWidth="md">
          <Container maxWidth="sm">
            <Card sx={{ textAlign: 'center', mt: 3, p: 4 }}>
              <Box textAlign="center">
                <Typography
                  variant="h6"
                  color="text.secondary"
                  fontWeight="normal"
                  sx={{ mb: 4 }}
                >
                  <img width="50%" height="50%" alt="bookingDliteLogo.png-logo" src={bookingDliteLogo} />
                </Typography>
                {/* {DEV_ENVIRONMENT && <DevLoginForm loginType={loginType} />} */}

                {errorMessage && (
                  <Typography
                    variant="h4"
                    color="text"
                    fontWeight="normal"
                    sx={{ mb: 4 }}
                  >
                    <FormControl sx={{ m: 3 }} error={true} variant="standard">
                      <FormLabel id="demo-error-radios">
                        {errorMessage}
                      </FormLabel>
                    </FormControl>
                  </Typography>
                )}
              </Box>
              {DEV_ENVIRONMENT && (
                <LoginByUserNamePassword loginType={loginType} />
              )}
              <Divider sx={{ my: 4 }}>OR</Divider>
              <Box textAlign="center">
                <FormControl variant="outlined" fullWidth>
                  <Box
                    textAlign="center"
                    display={'flex'}
                    justifyContent={'center'}
                  >
                    <GoogleOAuthProvider
                      clientId={AUTH_CONFIG.GOOGLE_CLIENT_ID}
                    >
                      <GoogleLogin
                        onSuccess={(response) => {
                          responseonSuccessGoogle(response);
                        }}
                        onError={() => {
                          responseonFailureGoogle(null);
                        }}
                        useOneTap={false}
                      />
                    </GoogleOAuthProvider>
                  </Box>
                </FormControl>
              </Box>

              {/* <Divider sx={{ my: 4 }}>OR</Divider>
              <FormControl variant="outlined" fullWidth>
                <Box textAlign="center">
                  <div
                    className={classes.msLoginButton}
                    onClick={() => handleLogin(instance)}
                  >
                    <svg
                      className={classes.msLogo}
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 48 48"
                      width="18px"
                      height="18px"
                    >
                      <path
                        fill="#ff5722"
                        d="M6 6H22V22H6z"
                        transform="rotate(-180 14 14)"
                      />
                      <path
                        fill="#4caf50"
                        d="M26 6H42V22H26z"
                        transform="rotate(-180 34 14)"
                      />
                      <path
                        fill="#ffc107"
                        d="M26 26H42V42H26z"
                        transform="rotate(-180 34 34)"
                      />
                      <path
                        fill="#03a9f4"
                        d="M6 26H22V42H6z"
                        transform="rotate(-180 14 34)"
                      />
                    </svg>
                    {t('login.signInWithMicrosoft')}
                  </div>
                </Box>
              </FormControl> */}
            </Card>
          </Container>
        </Container>
      </MainContent>
    </>
  );
};

export default Login;
